import React, { useMemo, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Users2, Briefcase, Clock, Calendar, CheckCircle, Hammer, ArrowRight, AlertCircle, LayoutDashboard } from 'lucide-react';
import { useAppContext } from '../../hooks/useAppContext';
import useAppStore from '../../store/useAppStore';
import { Report, ProjectDetails, ProjectWorkflowStatus, ReportType } from '../../types';
import { differenceInDays } from 'date-fns';
import toast from 'react-hot-toast';

const TimeCounter: React.FC<{ startDateString: string }> = ({ startDateString }) => {
    if (!startDateString) return null;
    const days = differenceInDays(new Date(), new Date(startDateString));
    if (days < 0) return <span>0 يوم</span>;
    return <span>{days} يوم</span>;
};

const getStatusBadge = (status?: ProjectWorkflowStatus) => {
    switch (status) {
        case ProjectWorkflowStatus.PendingTeamAcceptance:
            return <span className="px-2 py-1 rounded-lg bg-amber-500/20 text-amber-400 border border-amber-500/30 text-xs font-medium">بانتظار موافقة الفريق</span>;
        case ProjectWorkflowStatus.InProgress:
            return <span className="px-2 py-1 rounded-lg bg-blue-500/20 text-blue-400 border border-blue-500/30 text-xs font-medium">قيد التنفيذ</span>;
        case ProjectWorkflowStatus.FinishingWorks:
            return <span className="px-2 py-1 rounded-lg bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 text-xs font-medium">أعمال التشطيبات</span>;
        case ProjectWorkflowStatus.ConcreteWorksDone:
            return <span className="px-2 py-1 rounded-lg bg-orange-500/20 text-orange-400 border border-orange-500/30 text-xs font-medium">بانتظار الدفعة الثانية</span>;
        case ProjectWorkflowStatus.TechnicallyCompleted:
            return <span className="px-2 py-1 rounded-lg bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-xs font-medium">مكتمل فنياً</span>;
        case ProjectWorkflowStatus.Finalized:
            return <span className="px-2 py-1 rounded-lg bg-green-500/20 text-green-400 border border-green-500/30 text-xs font-medium">مكتمل</span>;
        default:
            return <span className="px-2 py-1 rounded-lg bg-slate-500/20 text-slate-400 border border-slate-500/30 text-xs font-medium">مسودة</span>;
    }
}

const ModernTeamProjectsScreen: React.FC = () => {
    const { user, t } = useAppContext();
    const { reports, currentUserLedTeam, acceptProjectAssignment } = useAppStore();
    const navigate = useNavigate();
    const location = useLocation();
    const [acceptingProjectId, setAcceptingProjectId] = useState<string | null>(null);

    const teamProjects = useMemo(() => {
        if (!currentUserLedTeam) return [];
        return reports.filter(r => r.type === ReportType.Project && r.assignedTeamId === currentUserLedTeam.id)
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }, [reports, currentUserLedTeam]);

    const statusParam = useMemo(() => new URLSearchParams(location.search).get('status'), [location.search]);
    const displayedProjects = useMemo(() => {
        switch (statusParam) {
            case 'pending-acceptance':
                return teamProjects.filter(p => p.projectWorkflowStatus === ProjectWorkflowStatus.PendingTeamAcceptance);
            case 'in-progress':
                return teamProjects.filter(p =>
                    p.projectWorkflowStatus === ProjectWorkflowStatus.InProgress ||
                    p.projectWorkflowStatus === ProjectWorkflowStatus.FinishingWorks ||
                    p.projectWorkflowStatus === ProjectWorkflowStatus.ConcreteWorksDone
                );
            case 'technical-completed':
                return teamProjects.filter(p => p.projectWorkflowStatus === ProjectWorkflowStatus.TechnicallyCompleted);
            case 'completed':
                return teamProjects.filter(p =>
                    p.projectWorkflowStatus === ProjectWorkflowStatus.TechnicallyCompleted ||
                    p.projectWorkflowStatus === ProjectWorkflowStatus.Finalized
                );
            case 'exceptions':
                return teamProjects.filter(p => {
                    const details = p.details as ProjectDetails;
                    return Array.isArray(details?.exceptions) && details.exceptions.length > 0;
                });
            default:
                return teamProjects;
        }
    }, [teamProjects, statusParam]);

    const kpis = useMemo(() => {
        const total = teamProjects.length;
        const pendingAcceptance = teamProjects.filter(p => p.projectWorkflowStatus === ProjectWorkflowStatus.PendingTeamAcceptance).length;
        const inProgress = teamProjects.filter(p =>
            p.projectWorkflowStatus === ProjectWorkflowStatus.InProgress ||
            p.projectWorkflowStatus === ProjectWorkflowStatus.FinishingWorks ||
            p.projectWorkflowStatus === ProjectWorkflowStatus.ConcreteWorksDone
        ).length;
        const completed = teamProjects.filter(p =>
            p.projectWorkflowStatus === ProjectWorkflowStatus.TechnicallyCompleted ||
            p.projectWorkflowStatus === ProjectWorkflowStatus.Finalized
        ).length;
        const withExceptions = teamProjects.filter(p => {
            const details = p.details as ProjectDetails;
            return Array.isArray(details?.exceptions) && details.exceptions.length > 0;
        }).length;
        return { total, pendingAcceptance, inProgress, completed, withExceptions };
    }, [teamProjects]);

    if (!currentUserLedTeam) {
        return (
            <div className="flex flex-col items-center justify-center h-full min-h-[60vh] text-slate-400">
                <Users2 size={64} className="mb-4 opacity-20" />
                <p>أنت لست قائد فريق.</p>
            </div>
        );
    }

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold modern-gradient-text mb-2">مشاريع الفريق</h1>
                    <p className="text-slate-400">إدارة ومتابعة مشاريع فريق {currentUserLedTeam?.name}</p>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/team-projects?status=completed')}
                        className={`px-4 py-2 rounded-xl border transition-all ${statusParam === 'completed' ? 'bg-blue-600 border-blue-500 text-white' : 'bg-slate-800/50 border-slate-700 text-slate-400 hover:text-white'}`}
                    >
                        المشاريع المكتملة
                    </button>
                    <button
                        onClick={() => navigate('/team-projects')}
                        className={`px-4 py-2 rounded-xl border transition-all ${!statusParam ? 'bg-blue-600 border-blue-500 text-white' : 'bg-slate-800/50 border-slate-700 text-slate-400 hover:text-white'}`}
                    >
                        الكل
                    </button>
                    <button
                        onClick={() => navigate('/')}
                        className="px-4 py-2 rounded-xl glass-card hover:bg-white/10 text-slate-300 transition-all flex items-center gap-2"
                    >
                        <ArrowRight size={18} />
                        <span>رجوع</span>
                    </button>
                </div>
            </div>

            {/* Team Info & KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="glass-card p-6 rounded-2xl flex flex-col justify-center">
                    <div className="flex items-center gap-4 mb-4">
                        <div className="p-3 rounded-xl bg-blue-500/20 text-blue-400">
                            <Users2 size={24} />
                        </div>
                        <div>
                            <p className="text-sm text-slate-400">الفريق</p>
                            <h3 className="text-lg font-bold text-white">{currentUserLedTeam?.name}</h3>
                        </div>
                    </div>
                    <div className="flex justify-between text-sm text-slate-400 border-t border-white/5 pt-4">
                        <span>القائد: {currentUserLedTeam?.leaderName}</span>
                        <span>{currentUserLedTeam?.members.length} أعضاء</span>
                    </div>
                </div>

                <Link to="/project-dashboard" className="glass-card p-6 rounded-2xl flex flex-col justify-center hover:bg-white/5 transition-all group">
                    <div className="flex items-center justify-between mb-2">
                        <div className="p-3 rounded-xl bg-purple-500/20 text-purple-400 group-hover:scale-110 transition-transform">
                            <Briefcase size={24} />
                        </div>
                        <span className="text-3xl font-bold text-white">{kpis.total}</span>
                    </div>
                    <p className="text-slate-400">إجمالي المشاريع</p>
                </Link>

                <Link to="/team-projects?status=pending-acceptance" className="glass-card p-6 rounded-2xl flex flex-col justify-center hover:bg-white/5 transition-all group">
                    <div className="flex items-center justify-between mb-2">
                        <div className="p-3 rounded-xl bg-amber-500/20 text-amber-400 group-hover:scale-110 transition-transform">
                            <AlertCircle size={24} />
                        </div>
                        <span className="text-3xl font-bold text-white">{kpis.pendingAcceptance}</span>
                    </div>
                    <p className="text-slate-400">بانتظار الموافقة</p>
                </Link>
            </div>

            {/* Projects Grid */}
            {displayedProjects.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {displayedProjects.map(project => {
                        const details = project.details as ProjectDetails;
                        return (
                            <div key={project.id} className="glass-card p-5 rounded-2xl hover:bg-slate-800/50 transition-all group relative overflow-hidden flex flex-col">
                                <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-blue-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity" />

                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        <h3 className="font-bold text-white truncate max-w-[200px]">{details.projectOwner}</h3>
                                        <p className="text-xs text-slate-500 font-mono">{project.id}</p>
                                    </div>
                                    {getStatusBadge(project.projectWorkflowStatus)}
                                </div>

                                <div className="space-y-3 text-sm text-slate-400 mb-6 flex-1">
                                    <div className="flex items-center gap-2">
                                        <Hammer size={14} className="text-blue-400" />
                                        <span>{details.size}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Calendar size={14} className="text-purple-400" />
                                        <span>البدء: {new Date(details.startDate).toLocaleDateString('ar-SA')}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Clock size={14} className="text-amber-400" />
                                        <span>منذ <TimeCounter startDateString={details.startDate} /></span>
                                    </div>
                                </div>

                                <div className="pt-4 border-t border-white/5">
                                    {project.projectWorkflowStatus === ProjectWorkflowStatus.PendingTeamAcceptance ? (
                                        <button
                                            className="w-full py-2.5 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-500 transition-colors flex items-center justify-center gap-2"
                                            disabled={acceptingProjectId === project.id}
                                            onClick={async () => {
                                                setAcceptingProjectId(project.id);
                                                try {
                                                    await acceptProjectAssignment(project.id);
                                                    toast.success('تم قبول المشروع بنجاح');
                                                } catch (e) {
                                                    toast.error('حدث خطأ أثناء الموافقة على المشروع');
                                                } finally {
                                                    setAcceptingProjectId(null);
                                                }
                                            }}
                                        >
                                            {acceptingProjectId === project.id ? (
                                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                            ) : (
                                                <CheckCircle size={18} />
                                            )}
                                            <span>قبول المشروع</span>
                                        </button>
                                    ) : (
                                        <div className="grid grid-cols-2 gap-3">
                                            <button
                                                onClick={() => navigate(`/reports/${project.id}`)}
                                                className="py-2 rounded-xl bg-slate-800 text-slate-300 hover:bg-slate-700 transition-colors text-sm font-medium"
                                            >
                                                التفاصيل
                                            </button>
                                            <button
                                                onClick={() => navigate(`/project-dashboard`)}
                                                className="py-2 rounded-xl bg-slate-800 text-slate-300 hover:bg-slate-700 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                                            >
                                                <LayoutDashboard size={14} />
                                                <span>لوحة المشاريع</span>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="glass-card p-12 rounded-2xl flex flex-col items-center justify-center text-center">
                    <Briefcase size={64} className="text-slate-600 mb-6 opacity-50" />
                    <h3 className="text-xl font-bold text-white mb-2">
                        {statusParam === 'pending-acceptance' ? t('noPendingTeamAcceptance') : t('noProjectReports')}
                    </h3>
                    <p className="text-slate-400 max-w-md mx-auto">
                        {statusParam === 'pending-acceptance' ? t('noPendingTeamAcceptanceMessage') : t('noProjectReportsMessage')}
                    </p>
                </div>
            )}
        </div>
    );
}

export default ModernTeamProjectsScreen;
